import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import {
  KIND_LABEL,
  KIND_LABEL_KEY,
  STATE_LABEL,
  STATE_LABEL_KEY,
  formatMinorAsString,
  useReceipts,
  type ReceiptLifecycleState,
} from '@/hooks/use-receipts';
import { cn } from '@/lib/utils';
import { useT } from '@/i18n/use-t';

function stateVariant(s: ReceiptLifecycleState) {
  switch (s) {
    case 'issued':
      return 'default' as const;
    case 'partially_returned':
      return 'outline' as const;
    case 'fully_returned':
      return 'success' as const;
    case 'locked':
      return 'destructive' as const;
    case 'expired':
    default:
      return 'outline' as const;
  }
}

/**
 * RL-377: the dashboard's sales tile deep-links here with the trading day it
 * measured, as `?finalisedFrom=<instant>&finalisedTo=<instant>`. Holding the
 * window in the URL is what makes refresh, sharing and browser Back land on the
 * same list. The chips below stay component state — they are a browsing choice,
 * not the drill-down — and changing one keeps the window in the URL.
 *
 * Returns null when the URL carries no window, or carries one that is not a
 * pair of parseable instants, so a mangled link degrades to the whole list
 * rather than to an empty one.
 */
function windowFromUrl(params: URLSearchParams): { from: string; to: string } | null {
  const from = params.get('finalisedFrom');
  const to = params.get('finalisedTo');
  if (from === null || to === null) return null;
  if (Number.isNaN(Date.parse(from)) || Number.isNaN(Date.parse(to))) return null;
  return { from, to };
}

export function ReceiptsListPage() {
  const t = useT('admin_ui');
  const [params] = useSearchParams();
  const finalisedWindow = windowFromUrl(params);
  const [filter, setFilter] = useState<'all' | 'operational' | ReceiptLifecycleState>(
    'operational',
  );
  const [source, setSource] = useState<'all' | 'only' | 'exclude'>('all');
  // SNX-1124: a dispute starts from the number on the customer's receipt.
  const [numberInput, setNumberInput] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');

  /**
   * YUKI-39: imported 1C history is a different kind of thing from a receipt this
   * platform issued — returnable, but never rung up here. An operator reconciling
   * a cutover wants one or the other, not both interleaved.
   */
  const sourceFilters: { value: 'all' | 'only' | 'exclude'; label: string }[] = [
    { value: 'all', label: t('receipts.list.source.all', 'All sources') },
    { value: 'exclude', label: t('receipts.list.source.issuedHere', 'Issued here') },
    { value: 'only', label: t('receipts.list.source.legacy', 'Legacy receipts') },
  ];

  const stateFilters: { value: 'all' | 'operational' | ReceiptLifecycleState; label: string }[] = [
    { value: 'operational', label: t('receipts.list.state.operational', 'Operational') },
    { value: 'all', label: t('receipts.list.state.all', 'All') },
    { value: 'issued', label: t('receipts.list.state.issued', 'Issued') },
    {
      value: 'partially_returned',
      label: t('receipts.list.state.partiallyReturned', 'Partially returned'),
    },
    { value: 'fully_returned', label: t('receipts.list.state.fullyReturned', 'Fully returned') },
    { value: 'locked', label: t('receipts.list.state.locked', 'Locked') },
  ];

  const stateFilter: ReceiptLifecycleState[] | undefined =
    filter === 'all'
      ? undefined
      : filter === 'operational'
        ? ['issued', 'partially_returned', 'locked']
        : [filter];

  const { data, isLoading } = useReceipts({
    pageSize: 100,
    // A number search looks in every state: a disputed sale may be returned.
    lifecycleState: receiptNumber ? undefined : stateFilter,
    ...(receiptNumber ? { receiptNumber } : {}),
    ...(source === 'all' ? {} : { legacy: source }),
    ...(finalisedWindow === null
      ? {}
      : { finalisedFrom: finalisedWindow.from, finalisedTo: finalisedWindow.to }),
  });
  const items = data?.items ?? [];

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('receipts.list.title', 'Receipts')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'receipts.list.intro',
            'Finalised transactions persisted by terminals. Use the lock action to fraud-hold a receipt; refund allocations advance the lifecycle state automatically.',
          )}
        </p>
      </header>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setReceiptNumber(numberInput.trim());
        }}
      >
        <label className="flex flex-col text-sm" htmlFor="receipt-number-search">
          {t('receipts.list.receiptNumber', 'Receipt number')}
          <input
            id="receipt-number-search"
            className="rounded-md border border-input px-3 py-1.5"
            value={numberInput}
            maxLength={64}
            onChange={(e) => setNumberInput(e.target.value)}
          />
        </label>
        <Button type="submit" variant="outline">
          {t('receipts.list.search', 'Search')}
        </Button>
      </form>

      <div className="flex flex-wrap gap-2">
        {sourceFilters.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setSource(opt.value)}
            className={cn(
              'rounded-md border px-3 py-1.5 text-sm',
              source === opt.value
                ? 'border-foreground bg-foreground text-background'
                : 'border-input hover:bg-muted',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-1 text-sm">
        {stateFilters.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={cn(
              'rounded-sm px-3 py-1.5 transition-colors',
              filter === opt.value
                ? 'bg-secondary font-medium text-secondary-foreground'
                : 'text-muted-foreground hover:bg-secondary/60',
            )}
            onClick={() => setFilter(opt.value)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/*
        The drill-down window, made visible so the list is never silently
        narrowed, and clickable so it can be dropped again. Everything in it is
        data or a glyph — no new English on a page mid-i18n-sweep; the word is
        the table header's own key.
      */}
      {finalisedWindow !== null && (
        <Link to="/receipts" className="inline-block" data-testid="receipts-window-filter">
          <Badge variant="outline" className="gap-1">
            {t('receipts.field.finalised', 'Finalised')}{' '}
            {new Date(finalisedWindow.from).toLocaleString()} —{' '}
            {new Date(finalisedWindow.to).toLocaleString()} ×
          </Badge>
        </Link>
      )}

      <div className="rounded-md border border-border bg-card">
        {isLoading ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {t('common.loading', 'Loading…')}
          </p>
        ) : items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">
            {t('receipts.list.empty', 'No receipts match the current filter.')}
          </p>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t('receipts.field.kind', 'Kind')}</TH>
                <TH>{t('receipts.field.state', 'State')}</TH>
                <TH>{t('receipts.field.finalised', 'Finalised')}</TH>
                <TH>{t('receipts.field.terminal', 'Terminal')}</TH>
                <TH className="text-right">{t('receipts.field.total', 'Total')}</TH>
                <TH className="text-right">{t('receipts.field.actions', 'Actions')}</TH>
              </TR>
            </THead>
            <TBody>
              {items.map((r) => (
                <TR key={r.id}>
                  <TD>{t(KIND_LABEL_KEY[r.receiptType], KIND_LABEL[r.receiptType])}</TD>
                  <TD>
                    <Badge variant={stateVariant(r.lifecycleState)}>
                      {t(STATE_LABEL_KEY[r.lifecycleState], STATE_LABEL[r.lifecycleState])}
                    </Badge>
                  </TD>
                  <TD className="font-mono text-xs">{new Date(r.finalisedAt).toLocaleString()}</TD>
                  <TD className="text-xs">
                    <span>{r.terminalLabel ?? r.issuingTerminalId.slice(-8)}</span>
                    {r.isLegacy && (
                      <Badge variant="outline" className="ml-2">
                        {t('receipts.list.legacyBadge', 'Legacy')}
                      </Badge>
                    )}
                  </TD>
                  <TD className="text-right font-mono text-xs">
                    {formatMinorAsString(r.totalMinor)}
                    {r.currency !== null && (
                      <span className="ml-1 text-muted-foreground">{r.currency}</span>
                    )}
                  </TD>
                  <TD className="text-right">
                    <Button asChild variant="ghost" size="sm">
                      <Link to={`/receipts/${r.id}`}>{t('receipts.action.open', 'Open')}</Link>
                    </Button>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </div>
    </div>
  );
}
